import { useDepositWithdrawAssetsDictionary } from '@/shared/api';
import { useMemo } from 'react';
import type { NetworkOption } from './types';
import type { SavedCryptoAddress } from '@/entities/crypto-address';

type Props = {
  savedCryptoAddress: SavedCryptoAddress;
};

type EditCryptoAddressFormData = {
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  networkOptions: NetworkOption[];
};

export const useEditCryptoAddressFormData = ({
  savedCryptoAddress,
}: Props): EditCryptoAddressFormData => {
  const {
    depositWithdrawAssetsData,
    depositWithdrawAssetsLoading,
    depositWithdrawAssetsError,
  } = useDepositWithdrawAssetsDictionary();

  const isReady = !!depositWithdrawAssetsData && !depositWithdrawAssetsLoading;
  const isLoading = !isReady && !depositWithdrawAssetsError;

  const cryptoNetworks = depositWithdrawAssetsData?.value.withdraw.crypto ?? [];

  const savedNetwork = useMemo(() => {
    if (!isReady) return undefined;

    return cryptoNetworks.find(
      network =>
        network.networkCode === savedCryptoAddress.networkCode ||
        network.networkName === savedCryptoAddress.networkName
    );
  }, [isReady, cryptoNetworks, savedCryptoAddress.networkCode]);

  const networkOptions: NetworkOption[] = useMemo(() => {
    if (!isReady || !savedNetwork) return [];

    return [
      {
        value: savedNetwork.networkName,
        code: savedNetwork.networkCode,
        name: savedNetwork.networkName,
      },
    ];
  }, [isReady, savedNetwork]);

  return {
    isLoading,
    isError: !!depositWithdrawAssetsError,
    error: depositWithdrawAssetsError,
    networkOptions,
  };
};
